import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Download, Search, Trash2, Loader2, LayoutDashboard } from "lucide-react";
import { useApp, API } from "@/context/AppContext";
import { STATUS_LABELS } from "@/i18n";
import { Logo } from "@/components/Logo";

const STATUSES = ["new", "contacted", "quoted", "completed", "closed"];
const STATUS_COLOR = {
  new: "#EAB308", contacted: "#3b82f6", quoted: "#a855f7", completed: "#006C35", closed: "#71717a",
};
const field = "w-full bg-[#0a0a0a] border border-[#27272a] text-white px-4 py-3 text-sm outline-none focus:border-[#EAB308] transition-colors";

const Admin = () => {
  const { tr, lang } = useApp();
  const a = tr.admin;
  const [token, setToken] = useState(() => localStorage.getItem("adminToken") || "");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [qs, st] = await Promise.all([
        axios.get(`${API}/admin/quotations`, { ...auth, params: { status: filter, q } }),
        axios.get(`${API}/admin/stats`, auth),
      ]);
      setRows(qs.data); setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) { setToken(""); localStorage.removeItem("adminToken"); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter, q]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      setToken(data.token); localStorage.setItem("adminToken", data.token);
      toast.success("Welcome, " + data.username);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const logout = () => { setToken(""); localStorage.removeItem("adminToken"); };

  const changeStatus = async (id, status) => {
    await axios.patch(`${API}/admin/quotations/${id}`, { status }, auth);
    toast.success("Updated"); load();
  };
  const del = async (id) => {
    await axios.delete(`${API}/admin/quotations/${id}`, auth);
    toast.success("Deleted"); setSelected(null); load();
  };
  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/quotations/export`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a"); link.href = url; link.download = "quotations.csv"; link.click();
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-md border border-[#27272a] bg-[#121212] p-8">
          <div className="flex justify-center mb-6"><Logo /></div>
          <h1 className="font-head text-2xl font-black text-white text-center mb-6">{a.login}</h1>
          <form onSubmit={login} className="space-y-4">
            <div><label className="text-xs text-zinc-400 mb-1.5 block">{a.username}</label>
              <input className={field} value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} data-testid="admin-username" autoComplete="username" /></div>
            <div><label className="text-xs text-zinc-400 mb-1.5 block">{a.password}</label>
              <input type="password" className={field} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} data-testid="admin-password" autoComplete="current-password" /></div>
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full bg-[#EAB308] text-[#050505] font-bold py-3.5 uppercase tracking-wide hover:bg-[#ca8a04] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : a.signin}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-head text-3xl font-black text-white flex items-center gap-3"><LayoutDashboard className="h-7 w-7 text-[#EAB308]" />{a.title}</h1>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="admin-export-btn" className="inline-flex items-center gap-2 border border-[#27272a] text-white px-4 py-2.5 text-sm hover:border-[#EAB308] transition-colors"><Download className="h-4 w-4" />{a.export}</button>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-[#27272a] text-white px-4 py-2.5 text-sm hover:border-red-500 hover:text-red-400 transition-colors"><LogOut className="h-4 w-4" />{a.logout}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
        <div className="border border-[#27272a] bg-[#121212] p-4"><p className="text-zinc-400 text-xs uppercase">Total</p><p className="text-2xl font-black text-white">{stats.total || 0}</p></div>
        {STATUSES.map((s) => (
          <div key={s} className="border border-[#27272a] bg-[#121212] p-4"><p className="text-xs uppercase" style={{ color: STATUS_COLOR[s] }}>{STATUS_LABELS[lang][s]}</p><p className="text-2xl font-black text-white">{stats[s] || 0}</p></div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-zinc-500" />
          <input className={`${field} ps-10`} placeholder={a.searchph} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
        </div>
        <select className={`${field} w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-filter">
          <option value="all">{a.all}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[lang][s]}</option>)}
        </select>
      </div>

      <div className="border border-[#27272a] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-table">
          <thead className="bg-[#0a0a0a] text-zinc-400 text-xs uppercase">
            <tr>
              <th className="text-start p-3">{tr.form.name}</th>
              <th className="text-start p-3">{tr.form.city}</th>
              <th className="text-start p-3">{tr.form.mobile}</th>
              <th className="text-start p-3">{tr.form.size}</th>
              <th className="text-start p-3">{a.status}</th>
              <th className="text-start p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">{a.noData}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-[#27272a] hover:bg-[#0a0a0a]" data-testid={`admin-row-${r.id}`}>
                <td className="p-3 text-white cursor-pointer" onClick={() => setSelected(r)}>{r.customer_name}{r.company_name ? ` · ${r.company_name}` : ""}</td>
                <td className="p-3 text-zinc-300">{r.city}</td>
                <td className="p-3 text-zinc-300" dir="ltr">{r.mobile}</td>
                <td className="p-3 text-zinc-300" dir="ltr">{r.tyre_size || "-"}</td>
                <td className="p-3">
                  <select value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)} data-testid={`status-select-${r.id}`}
                    className="bg-[#0a0a0a] border px-2 py-1 text-xs" style={{ borderColor: STATUS_COLOR[r.status], color: STATUS_COLOR[r.status] }}>
                    {STATUSES.map((s) => <option key={s} value={s} className="text-white bg-[#0a0a0a]">{STATUS_LABELS[lang][s]}</option>)}
                  </select>
                </td>
                <td className="p-3"><button onClick={() => del(r.id)} data-testid={`delete-${r.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#121212] border border-[#27272a] max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-detail-modal">
            <h3 className="font-head text-xl font-bold text-white mb-4">{selected.customer_name}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.entries({
                [tr.form.company]: selected.company_name, [tr.form.mobile]: selected.mobile, [tr.form.whatsapp]: selected.whatsapp,
                [tr.form.email]: selected.email, [tr.form.city]: selected.city,
                [tr.form.make]: selected.vehicle_make, [tr.form.model]: selected.vehicle_model, [tr.form.year]: selected.vehicle_year,
                [tr.form.size]: selected.tyre_size, [tr.form.brand]: selected.preferred_brand, [tr.form.category]: selected.tyre_category,
                [tr.form.qty]: selected.quantity, [tr.form.delivery]: selected.delivery_location, [tr.form.date]: selected.required_date,
                [tr.form.contact]: selected.preferred_contact,
              }).filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><p className="text-zinc-500 text-xs">{k}</p><p className="text-white" dir="auto">{v}</p></div>
              ))}
            </div>
            {selected.notes && <div className="mt-3"><p className="text-zinc-500 text-xs">{tr.form.notes}</p><p className="text-white">{selected.notes}</p></div>}
            <button onClick={() => setSelected(null)} className="mt-6 w-full border border-[#27272a] text-white py-2.5 hover:border-[#EAB308] transition-colors">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
